import { createComponentStore } from '../../utils/createComponentStore';

// ---- Utility functions (module-scope, exported) ----

const keyAuto = () =>
  'conditional' + (Math.floor(Math.random() * 90000000) + 10000).toString();

const logicData = () => ({
  key: keyAuto(),
  outputType: 'TEXT',
  statement: {
    type: 'IF',
    content: {
      condition: [
        {
          operators: [
            {
              logic: 'undefined',
              key: '',
              function: 'EQUAL',
              value: '',
            },
          ],
          then: {
            statement: {
              type: 'VALUE',
              content: 'CONTINUE',
            },
          },
        },
        {
          then: {
            statement: {
              type: 'VALUE',
              content: 'STOP',
            },
          },
        },
      ],
    },
  },
});

// ---- Store definition via factory ----

const initData = () => ({
  conditions: [logicData()],
});

const { getStore: getConditionalWorkflowStore, defaultStore } = createComponentStore({
  type: 'conditional_workflow',
  endpoint: 'conditional-workflow',
  registryKey: 'conditional-workflow',
  initData,

  onLoadData: (rawData, component) => {
    const cd = component.componentData;
    if (cd && Array.isArray(cd.data)) return { conditions: cd.data };
    if (rawData && Array.isArray(rawData.conditions)) return rawData;
    if (Array.isArray(rawData)) return { conditions: rawData };
    return initData();
  },

  extraState: (/* set, get */) => ({
    placeholders: [],
    positions: {
      indexLogic: null,
      indexStatement: null,
      indexCondition: null,
      type: null,
    },
  }),

  extraActions: (set, get) => ({
    // ---- Backward-compat aliases ----

    setConditionalWorkflowTemplate: (template) => get().setComponentData(template),

    setConditionalWorkflowName: (name) => set({ name }),

    setData: (data) => set({ data }),

    // ---- Domain actions ----

    setCondition: (content, indexLogic, indexStatement, indexCondition, type) => {
      set((s) => {
        const data = JSON.parse(JSON.stringify(s.data));
        const target =
          data.conditions[indexLogic].statement.content.condition[indexStatement].operators[indexCondition];
        switch (type) {
          case 'logic':
            target.logic = content;
            break;
          case 'key':
            target.key = content ? content.trim() : '';
            break;
          case 'function':
            target.function = content;
            break;
          case 'value':
            target.value = content;
            break;
        }
        return { data };
      });
    },

    setThenContent: (content, indexLogic, indexStatement) => {
      set((s) => {
        const data = JSON.parse(JSON.stringify(s.data));
        data.conditions[indexLogic].statement.content.condition[indexStatement].then.statement.content = content;
        return { data };
      });
    },

    addStatement: (indexLogic) => {
      set((s) => {
        const data = JSON.parse(JSON.stringify(s.data));
        const obj = {
          operators: [
            {
              logic: 'OR',
              key: '',
              function: '',
              value: '',
            },
          ],
          then: {
            statement: {
              type: 'VALUE',
              content: '',
            },
          },
        };
        const { condition } = data.conditions[indexLogic].statement.content;
        condition.splice(condition.length - 1, 0, obj);
        return { data };
      });
    },

    addCondition: (indexLogic, indexStatement) => {
      set((s) => {
        const data = JSON.parse(JSON.stringify(s.data));
        data.conditions[indexLogic].statement.content.condition[indexStatement].operators.push({
          logic: 'OR',
          key: '',
          function: 'EQUAL',
          value: '',
        });
        return { data };
      });
    },

    setLogic: (content, index, type) => {
      set((s) => {
        const data = JSON.parse(JSON.stringify(s.data));
        switch (type) {
          case 'key':
            data.conditions[index].key = content;
            break;
          case 'outputType':
            data.conditions[index].outputType = content;
            break;
        }
        return { data };
      });
    },

    addLogic: () => {
      set((s) => ({
        data: { ...s.data, conditions: [...s.data.conditions, logicData()] },
      }));
    },

    removeLogic: (index) => {
      set((s) => ({
        data: { ...s.data, conditions: s.data.conditions.filter((_, i) => i !== index) },
      }));
    },

    copyThisLogic: (index) => {
      set((s) => {
        const copy = JSON.parse(JSON.stringify(s.data.conditions[index]));
        copy.key = keyAuto();
        return { data: { ...s.data, conditions: [...s.data.conditions, copy] } };
      });
    },

    removeCondition: (indexLogic, indexStatement, indexCondition) => {
      set((s) => {
        const data = JSON.parse(JSON.stringify(s.data));
        data.conditions[indexLogic].statement.content.condition[indexStatement].operators.splice(
          indexCondition,
          1
        );
        return { data };
      });
    },

    removeStatement: (indexLogic, indexStatement) => {
      set((s) => {
        const data = JSON.parse(JSON.stringify(s.data));
        data.conditions[indexLogic].statement.content.condition.splice(indexStatement, 1);
        return { data };
      });
    },

    setPlaceholders: (placeholders) => set({ placeholders }),

    setPositions: (positions) =>
      set({
        placeholders: [],
        positions,
      }),

    // Override getTemplate to include placeholders and positions
    getTemplate: () => {
      const s = get();
      const dataCopy = JSON.parse(JSON.stringify(s.data));
      return {
        componentId: s.componentId,
        name: s.name,
        data: dataCopy,
        type: s.type,
        lastModified: s.lastModified,
        placeholders: JSON.parse(JSON.stringify(s.placeholders)),
        positions: JSON.parse(JSON.stringify(s.positions)),
      };
    },

    // Expose utilities on the store for any consumers that access them via getState()
    logicData,
    keyAuto,
  }),
});

export { getConditionalWorkflowStore, logicData, keyAuto };
export default defaultStore;
